import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Radio,
  RadioGroup,
  Spinner,
  Text,
} from "@fluentui/react-components";
import { useSelectOrgDialogText } from "./SelectOrgDialog.i18n";
import type {
  SelectOrgDialogViewModel,
  SelectOrgTarget,
} from "./SelectOrgDialog.ViewModel.types";
import "./SelectOrgDialog.scss";

interface SelectOrgDialogProps {
  target: SelectOrgTarget | null;
  vm: SelectOrgDialogViewModel;
}

const SelectOrgDialog: React.FC<SelectOrgDialogProps> = ({ target, vm }) => {
  const text = useSelectOrgDialogText(target?.sourceDisplayName ?? "");

  return (
    <Dialog
      open={target !== null}
      onOpenChange={(_, data) => {
        if (!data.open) vm.cancel();
      }}
    >
      <DialogSurface className="select-org-dialog__surface">
        <DialogBody>
          <DialogTitle>{text.title}</DialogTitle>

          <DialogContent>
            <Text size={200} block className="select-org-dialog__description">
              {text.description}
            </Text>

            {vm.isLoading && <Spinner label={text.loadingOrgs} />}

            {vm.hasLoadError && (
              <Text role="alert" className="select-org-dialog__error">
                {text.loadError}
              </Text>
            )}

            {vm.hasNoOrgs && <Text>{text.noOrgsFound}</Text>}

            {vm.orgs.length > 0 && (
              <RadioGroup
                value={vm.selectedOrgId ?? ""}
                onChange={(_, data) => vm.setSelectedOrgId(data.value)}
              >
                {vm.orgs.map((org) => (
                  <Radio
                    key={org.id}
                    value={org.id}
                    label={
                      <span>
                        <strong>{org.friendlyName}</strong>
                        <br />
                        <Text size={200} className="select-org-dialog__org-url">
                          {org.url}
                        </Text>
                      </span>
                    }
                  />
                ))}
              </RadioGroup>
            )}

            {vm.hasSubmitError && (
              <Text role="alert" className="select-org-dialog__error">
                {text.submitError}
              </Text>
            )}
          </DialogContent>

          <DialogActions fluid>
            <Button appearance="secondary" onClick={vm.cancel}>
              {text.cancelButton}
            </Button>
            <Button
              appearance="primary"
              disabled={!vm.canSubmit}
              onClick={() => void vm.submit()}
            >
              {vm.isSubmitting ? <Spinner size="tiny" /> : text.selectButton}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SelectOrgDialog;
